import React from 'react';
import {
  HashRouter,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import { TemplateSignIn } from './TemplateSignIn';
import DashboardTemplate from './mui-dashboard/Dashboard';
import { GoogleMapsProvider } from './GoogleMapsProvider';
import UsersPage from './pages/UsersPage';
import RentsPage from './pages/RentsPage';
import PowerbanksPage from './pages/PowerbanksPage';
import StationsPage from './pages/StationsPage';
import StationCreate from './pages/StationCreate';
import FinancePage from './pages/FinancePage';
import TariffsPage from './pages/TariffsPage';
import MerchantsPage from './pages/MerchantsPage';
import MerchantCreate from './pages/MerchantCreate';
import DesignSystemPage from './pages/DesignSystemPage';
import DefaultDashboardContent from './pages/DefaultDashboardContent';
import ServiceTicketsPage from './pages/ServiceTicketsPage';
import ServiceTicketCreatePage from './pages/ServiceTicketCreatePage';
import ServiceTicketDetailPage from './pages/ServiceTicketDetailPage';
import ServiceTechniciansPage from './pages/ServiceTechniciansPage';
import ServiceTechnicianCreatePage from './pages/ServiceTechnicianCreatePage';
import DialogsProvider from './mui-crud/hooks/useDialogs/DialogsProvider';
import NotificationsProvider from './mui-crud/hooks/useNotifications/NotificationsProvider';
import { clearAuthStorage, isAccessTokenExpired } from './utils/authSession';

function isAuthenticated() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (isAccessTokenExpired(token)) {
      clearAuthStorage();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function getRole() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('role') || '' : '';
  } catch {
    return '';
  }
}

function ProtectedRoute({ component: Component, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) => (isAuthenticated() ? <Component {...props} /> : <Redirect to="/login" />)}
    />
  );
}

/** Route visible only for given roles; otherwise redirect to dashboard. */
function RoleProtectedRoute({ component: Component, allowedRoles, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated()) return <Redirect to="/login" />;
        const role = getRole();
        if (!allowedRoles.includes(role)) return <Redirect to="/" />;
        return <Component {...props} />;
      }}
    />
  );
}

/**
 * App uses only template UI. No legacy views/components.
 * Each route renders a page built from template components + API.
 */
export function TemplateApp() {
  return (
    <HashRouter>
      <Switch>
        <Route path="/login" component={TemplateSignIn} />
        <ProtectedRoute
          path="/"
          component={() => (
            <DialogsProvider>
              <NotificationsProvider>
                <GoogleMapsProvider>
                  <DashboardTemplate>
                    <Switch>
                    <RoleProtectedRoute path="/users" component={UsersPage} allowedRoles={['SUPER_ADMIN', 'OPERATOR']} />
                    <Route path="/rents" component={RentsPage} />
                    <Route path="/powerBank" component={PowerbanksPage} />
                    <Route path="/finance" component={FinancePage} />
                    <Route path="/station/new" component={StationCreate} />
                    <Route path="/station/edit/:stationNumber" component={StationCreate} />
                    <Route path="/station" component={StationsPage} />
                    <RoleProtectedRoute
                      path="/service/tickets/new"
                      component={ServiceTicketCreatePage}
                      allowedRoles={['SUPER_ADMIN', 'OPERATOR']}
                    />
                    <RoleProtectedRoute
                      path="/service/tickets/:id"
                      component={ServiceTicketDetailPage}
                      allowedRoles={['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'TECHNICIAN']}
                    />
                    <RoleProtectedRoute
                      path="/service/tickets"
                      component={ServiceTicketsPage}
                      allowedRoles={['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'TECHNICIAN']}
                    />
                    <RoleProtectedRoute
                      path="/service/technicians/new"
                      component={ServiceTechnicianCreatePage}
                      allowedRoles={['SUPER_ADMIN', 'ADMIN']}
                    />
                    <RoleProtectedRoute
                      path="/service/technicians"
                      component={ServiceTechniciansPage}
                      allowedRoles={['SUPER_ADMIN', 'ADMIN', 'OPERATOR']}
                    />
                    <Redirect from="/service/qa" to="/service/tickets?stage=DONE" />
                    {/* Create/delete tariff APIs missing on BE — route disabled until restored */}
                    <Redirect from="/tariff/new" to="/tariff" />
                    <RoleProtectedRoute path="/tariff" component={TariffsPage} allowedRoles={['SUPER_ADMIN']} />
                    <RoleProtectedRoute path="/merchant/new" component={MerchantCreate} allowedRoles={['ADMIN', 'SUPER_ADMIN']} />
                    <RoleProtectedRoute path="/merchants" component={MerchantsPage} allowedRoles={['ADMIN', 'SUPER_ADMIN']} />
                    <RoleProtectedRoute path="/design-system" component={DesignSystemPage} allowedRoles={['SUPER_ADMIN']} />
                    <Route path="/" component={DefaultDashboardContent} />
                  </Switch>
                </DashboardTemplate>
              </GoogleMapsProvider>
            </NotificationsProvider>
          </DialogsProvider>
        )}
        />
        <Redirect to={isAuthenticated() ? '/' : '/login'} />
      </Switch>
    </HashRouter>
  );
}
